import React, { useState, useEffect } from 'react';
import { Typography, Row, Col, Card, Spin, message, Grid, Divider, List, Badge, Button, Space } from 'antd';
import { CalendarOutlined, MessageOutlined, SnippetsOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/authService';
import { User } from '../services/userService';
import { trichDanService } from '../services/trichDanService';
import { congViecService, CongViecDto } from '../services/congViecService';
import solarLunar from 'solarlunar';
import dayjs from 'dayjs';
import './Dashboard.css';

const { Title, Text, Paragraph } = Typography;
const { useBreakpoint } = Grid;

const CAN_MAP: { [key: string]: string } = { '甲': 'Giáp', '乙': 'Ất', '丙': 'Bính', '丁': 'Đinh', '戊': 'Mậu', '己': 'Kỷ', '庚': 'Canh', '辛': 'Tân', '壬': 'Nhâm', '癸': 'Quý' };
const CHI_MAP: { [key: string]: string } = { '子': 'Tý', '丑': 'Sửu', '寅': 'Dần', '卯': 'Mão', '辰': 'Thìn', '巳': 'Tỵ', '午': 'Ngọ', '未': 'Mùi', '申': 'Thân', '酉': 'Dậu', '戌': 'Tuất', '亥': 'Hợi' };


const Dashboard: React.FC = () => {
    const [user, setUser] = useState<User | null>(null);
    const [quote, setQuote] = useState('');
    const [lunarDate, setLunarDate] = useState('');
    const [actionableTasks, setActionableTasks] = useState<CongViecDto[]>([]);
    const [loading, setLoading] = useState(true);
    const screens = useBreakpoint();
    const navigate = useNavigate();

    useEffect(() => {
        const fetchDashboardData = async () => {
            try {
                const [profileResponse, quoteResponse, tasksResponse] = await Promise.all([
                    authService.getProfile(),
                    trichDanService.getRandomQuote(),
                    congViecService.getTasks()
                ]);

                if (profileResponse) {
                    setUser(profileResponse);
                }

                if (quoteResponse.data?.noiDung) {
                    setQuote(quoteResponse.data.noiDung);
                }
                
                if (tasksResponse.data && profileResponse?.macc) {
                    const allTasks: CongViecDto[] = tasksResponse.data;
                    const currentUserMacc = profileResponse.macc;
                    const filteredTasks = allTasks.filter((task: CongViecDto) => 
                        (task.maNguoiXuLyHienTai === currentUserMacc && (task.trangThai === 0 || task.trangThai === 4)) || 
                        (task.maNguoiTao === currentUserMacc && task.trangThai === 1 && task.maNguoiXuLyHienTai === currentUserMacc)
                    ).slice(0, 5); // Take only top 5
                    setActionableTasks(filteredTasks);
                }

                const today = new Date();
                const lunar = solarLunar.solar2lunar(today.getFullYear(), today.getMonth() + 1, today.getDate());
                
                const vietnameseCan = CAN_MAP[lunar.gzYear[0]];
                const vietnameseChi = CHI_MAP[lunar.gzYear[1]];
                const vietnameseYearName = `${vietnameseCan} ${vietnameseChi}`;

                const lunarDateString = `Ngày ${lunar.lDay} tháng ${lunar.lMonth} năm ${vietnameseYearName}`;
                setLunarDate(lunarDateString);

            } catch (error) {
                message.error('Không thể tải dữ liệu cho dashboard.');
            } finally {
                setLoading(false);
            }
        };

        fetchDashboardData();
    }, []);

    const getDeadlineStatusStyles = (deadlineDate?: string, status?: number) => {
        let textColor = 'inherit';
        const isDone = status === 2 || status === 3;

        if (deadlineDate && !isDone) {
            const deadline = dayjs(deadlineDate);
            if (dayjs().isAfter(deadline)) {
                textColor = '#ff4d4f'; // Overdue
            } else if (deadline.diff(dayjs(), 'day') <= 2) {
                textColor = '#faad14'; // Nearing
            }
        }
        return { textColor };
    };

    const today = dayjs();

    return (
        <Spin spinning={loading} size="large">
            <div className="dashboard-container" style={{
                maxWidth: screens.xxl ? '2000px' : screens.xl ? '1400px' : screens.lg ? '1200px' : '100%',
                margin: '0 auto',
                padding: screens.xs ? '16px' : '24px',
                width: '100%'
            }}>
                <Card 
                    className="dashboard-card"
                    bodyStyle={{ 
                        padding: screens.xs ? '20px 16px' : '28px 24px',
                        textAlign: 'center',
                        background: 'linear-gradient(135deg, #f8faff 0%, #f0f7ff 100%)',
                        borderRadius: '12px',
                        border: '1px solid #e6f0ff',
                        maxWidth: '100%',
                        margin: '0 auto'
                    }}
                >
                    <div style={{ 
                        marginBottom: '24px',
                        position: 'relative',
                        paddingBottom: '20px'
                    }}>
                        <div style={{
                            content: '""',
                            position: 'absolute',
                            bottom: 0,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            width: '60px',
                            height: '3px',
                            background: 'linear-gradient(90deg, #1890ff 0%, #40a9ff 100%)',
                            borderRadius: '3px'
                        }}></div>
                        <Title level={screens.xs ? 4 : 2} style={{ marginBottom: '8px' }}>
                            Chào mừng <span className="user-name">{user?.employeeName}</span> trở lại,
                        </Title>
                        <Text style={{ 
                            fontSize: screens.xs ? '1rem' : '1.1rem', 
                            color: '#595959',
                            fontWeight: 400,
                            lineHeight: '1.6',
                            display: 'block'
                        }}>
                            Chúc bạn một ngày làm việc hiệu quả!
                        </Text>
                    </div>
                    
                    <Divider style={{ margin: '24px 0' }} />
                    
                    <Row gutter={[32, 24]} align="middle" style={{ marginBottom: '24px' }}>
                        <Col xs={24} md={12} lg={10} xl={8}>
                            <Card 
                                className="calendar-widget" 
                                style={{ 
                                    height: '100%',
                                    borderRadius: '8px',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                                }}
                                bodyStyle={{ padding: '16px' }}
                            >
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <div style={{
                                        fontSize: '2.5rem',
                                        fontWeight: 'bold',
                                        color: '#1890ff',
                                        minWidth: '60px',
                                        textAlign: 'center'
                                    }}>
                                        {today.format('DD')}
                                    </div>
                                    <div style={{ marginLeft: '16px' }}>
                                        <div style={{ 
                                            fontSize: '1.1rem',
                                            fontWeight: 500,
                                            color: '#333'
                                        }}>
                                            {today.format('dddd').charAt(0).toUpperCase() + today.format('dddd').slice(1)}
                                        </div>
                                        <div style={{ 
                                            fontSize: '1rem',
                                            color: '#333',
                                            margin: '4px 0'
                                        }}>
                                            {today.format('[Tháng] MM, YYYY')}
                                        </div>
                                        <div style={{ 
                                            fontSize: '1rem',
                                            color: '#333'
                                        }}>
                                            {lunarDate}
                                        </div>
                                    </div>
                                </div>
                            </Card>
                        </Col>
                        <Col xs={24} md={12} lg={14} xl={16}>
                            <Card 
                                className="quote-widget"
                                style={{
                                    height: '100%',
                                    borderRadius: '8px',
                                    background: '#f9f9f9',
                                    border: '1px solid #f0f0f0'
                                }}
                                bodyStyle={{ padding: '16px' }}
                            >
                                <blockquote style={{
                                    margin: 0,
                                    fontStyle: 'italic',
                                    fontSize: screens.xs ? '1rem' : '1.2rem',
                                    color: '#333',
                                    lineHeight: '1.6',
                                    position: 'relative',
                                    padding: '8px 0 8px 20px',
                                    borderLeft: '3px solid #1890ff'
                                }}>
                                    "{quote || 'Đang tải...'}"
                                </blockquote>
                            </Card>
                        </Col>
                    </Row>

                    <Divider style={{ margin: '16px 0 24px' }}>
                        <Space>
                            <SnippetsOutlined style={{ color: '#1890ff' }} />
                            {actionableTasks.length > 0 && (
                                <Badge count={actionableTasks.length} style={{ backgroundColor: '#1890ff' }} />
                            )}
                            <Text strong>Công việc cần xử lý</Text>
                        </Space>
                    </Divider>

                    <List
                        className="dashboard-task-list"
                        itemLayout="horizontal"
                        dataSource={actionableTasks}
                        style={{
                            backgroundColor: '#fff',
                            borderRadius: '8px',
                            padding: '8px 0',
                            boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                        }}
                        renderItem={(item) => {
                            const { textColor } = getDeadlineStatusStyles(item.ngayHetHan, item.trangThai);
                            return (
                                <List.Item 
                                    style={{
                                        padding: '12px 16px',
                                        borderBottom: '1px solid #f0f0f0',
                                        cursor: 'pointer',
                                        transition: 'all 0.3s',
                                    }}
                                    onMouseEnter={(e) => {
                                        const target = e.currentTarget as HTMLElement;
                                        target.style.backgroundColor = '#f9f9f9';
                                    }}
                                    onMouseLeave={(e) => {
                                        const target = e.currentTarget as HTMLElement;
                                        target.style.backgroundColor = '';
                                    }}
                                    onClick={() => navigate('/cong-viec')}
                                >
                                    <List.Item.Meta
                                        title={
                                            <div style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center'
                                            }}>
                                                <Text ellipsis style={{ maxWidth: '70%', fontSize: '1.05rem' }}>{item.tieuDe}</Text>
                                                {item.ngayHetHan && (
                                                    <div style={{ 
                                                        color: textColor, 
                                                        fontWeight: 500,
                                                        fontSize: '1rem',
                                                        minWidth: '80px',
                                                        textAlign: 'right'
                                                    }}>
                                                        Hạn: {dayjs(item.ngayHetHan).format('DD/MM')}
                                                    </div>
                                                )}
                                            </div>
                                        }
                                        description={
                                            <Text type="secondary" style={{ fontSize: '0.95rem' }}>
                                                Từ: {item.tenNguoiTao} • {dayjs(item.ngayTao).format('DD/MM/YYYY')}
                                            </Text>
                                        }
                                    />
                                </List.Item>
                            );
                        }}
                        footer={
                            actionableTasks.length > 0 ? (
                                <div style={{ textAlign: 'center', marginTop: '16px' }}>
                                    <Button 
                                        type="primary" 
                                        onClick={() => navigate('/cong-viec')}
                                        icon={<SnippetsOutlined />}
                                    >
                                        Xem tất cả và xử lý
                                    </Button>
                                </div>
                            ) : (
                                <div style={{ textAlign: 'center', padding: '16px 0' }}>
                                    <Text type="secondary">Không có công việc nào cần xử lý.</Text>
                                </div>
                            )
                        }
                    />
                </Card>
            </div>
        </Spin>
    );
};

export default Dashboard;
